import { useState } from "react";
import { useForm } from "react-hook-form";
import Input from "../../Shared/Input";
import PrimaryButton from "../../Shared/PrimaryButton";
import AlertRed from "../../Shared/AlertRed";
import AlertModal from "../../Shared/AlertModal";
import { useRotateCalendarFeed } from "../../../hooks/useCalendarFeed";
import { useToast } from "../../../hooks/useToast";

/**
 * Password-gated "Regenerate link" flow, shared by the full calendar-feed
 * view (feedUrl known) and the "link hidden" return-visit view (feedUrl
 * null). Renders its own password field + submit button, an AlertModal
 * confirmation, the rotate mutation, and 401 "Invalid password" handling.
 *
 * @param {() => void} [onSuccess] - called after a successful rotation
 *   (e.g. to collapse a "revealed" state in the caller)
 * @param {string} [heading] - section heading text, since the hidden-link
 *   view and the full view want slightly different framing
 * @param {string} [description] - section description text
 */
const RegenerateSection = ({
  onSuccess,
  heading = "Regenerate link",
  description = "Enter your password to create a new link.",
}) => {
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingPassword, setPendingPassword] = useState(null);
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { password: "" } });

  const { mutate: doRotate, isPending: isRotating } = useRotateCalendarFeed();

  const onSubmit = (data) => {
    setError(null);
    setPendingPassword(data.password);
    setIsModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (!pendingPassword || isRotating) return;
    doRotate(
      { password: pendingPassword },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          reset({ password: "" });
          toast.success(
            "Link Regenerated",
            "Your calendar feed link has been regenerated"
          );
          onSuccess?.();
        },
        onError: (err) => {
          setIsModalOpen(false);
          setError({
            title: "Error",
            message:
              err.response?.data?.error?.message ||
              "Failed to regenerate calendar feed link",
          });
        },
      }
    );
    // Don't keep the plaintext password in state after dispatching
    setPendingPassword(null);
  };

  const handleModalDismiss = () => {
    setIsModalOpen(false);
    setPendingPassword(null);
  };

  return (
    <>
      <div>
        <h3 className="text-sm font-semibold text-ink-900">{heading}</h3>
        <p className="mt-1 text-sm text-ink-600">{description}</p>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mt-4 max-w-sm space-y-4"
        >
          <Input
            id="calendarFeedPassword"
            name="password"
            label="Your password"
            type="password"
            errormessage={errors?.password?.message}
            {...register("password", {
              required: "Password is required to confirm this action",
            })}
          />
          <PrimaryButton colour="red" type="submit" disabled={isRotating}>
            {isRotating ? "Regenerating…" : "Regenerate link"}
          </PrimaryButton>
        </form>

        {error ? (
          <div className="mt-4">
            <AlertRed title={error.title} message={error.message} />
          </div>
        ) : null}
      </div>

      <AlertModal
        isOpen={isModalOpen}
        modalTitle="Regenerate your calendar feed link?"
        modalMessage="This breaks your old link — any connected calendars stop updating until you re-subscribe."
        primaryCaption="Regenerate"
        secondaryCaption="Cancel"
        primaryClickHandler={handleModalConfirm}
        dismissModalHandler={handleModalDismiss}
        primaryButtonColour="red"
        isPrimaryLoading={isRotating}
      />
    </>
  );
};

export default RegenerateSection;
